import { Container } from '@sparemin/blockhead';
import React, { forwardRef, useImperativeHandle } from 'react';
import { ClipSelectDetailCardProvider } from './ClipSelectDetailCardContext';
import ClipSelectDetailCardPreview from './ClipSelectDetailCardPreview';
import ClipSelectDetailEditPanel from './ClipSelectDetailEditPanel';
import ClipSelectDetailLoadingOverlay from './ClipSelectDetailLoadingOverlay';
import useClipSelectDetailCardController from './useClipSelectDetailCardController';
import { cardBlock as block } from './utils';

export interface ClipSelectDetailCardInstance {
  isPlaying: boolean;
  play: () => void;
  pause: () => void;
}

export interface ClipSelectDetailCardProps {
  id: number;
  onPlaybackChange: (isPlaying: boolean) => void;
}

const ClipSelectDetailCard = forwardRef<
  ClipSelectDetailCardInstance,
  ClipSelectDetailCardProps
>(({ id, onPlaybackChange }, ref) => {
  const {
    aspectRatioName,
    eddyRef,
    ctaIntent,
    isEddyReady,
    isInitialized,
    isPlaying,
    isPreviewReady,
    isPreviewLoading,
    lastPlaybackPosSec,
    handleOpenAdvancedEditor,
    handleEddyReady,
    handleEddySeek,
    handleCloseLoadingOverlay,
    handlePreviewDestroy,
    handlePlaybackChange,
    handlePreviewReady,
    handlePreviewSeek,
    handleSubmit,
    handleTranscriptUpdate,
    previewRef,
    previewWidgetId,
    showLoadingOverlay,
  } = useClipSelectDetailCardController({
    suggestionId: id,
    onPlaybackChange,
  });

  useImperativeHandle(
    ref,
    () => ({
      isPlaying,
      play: () => handlePlaybackChange(true),
      pause: () => handlePlaybackChange(false),
    }),
    [isPlaying, handlePlaybackChange],
  );

  return (
    <ClipSelectDetailCardProvider
      suggestionId={id}
      {...{ isInitialized, isPlaying, isPreviewReady, isEddyReady }}
    >
      <Container
        className={block({ [aspectRatioName]: !!aspectRatioName })}
        borderWeight="regular"
        cornerRadius={24}
        gutter={0}
      >
        <div className={block('left-panel')}>
          <ClipSelectDetailCardPreview
            isLoading={isPreviewLoading}
            lastPlaybackPos={lastPlaybackPosSec}
            onDestroy={handlePreviewDestroy}
            onPlaybackChange={handlePlaybackChange}
            onReady={handlePreviewReady}
            onSeek={handlePreviewSeek}
            ref={previewRef}
            widgetId={previewWidgetId}
          />
        </div>
        <div className={block('right-panel')}>
          <ClipSelectDetailEditPanel
            onAdvancedEditor={handleOpenAdvancedEditor}
            onPlaybackChange={handlePlaybackChange}
            onReady={handleEddyReady}
            onSeek={handleEddySeek}
            onSubmit={handleSubmit}
            onTranscriptUpdate={handleTranscriptUpdate}
            ref={eddyRef}
          />
        </div>
        {showLoadingOverlay && (
          <ClipSelectDetailLoadingOverlay
            onClose={handleCloseLoadingOverlay}
            variant={ctaIntent}
          />
        )}
      </Container>
    </ClipSelectDetailCardProvider>
  );
});

export default ClipSelectDetailCard;
